import { useEffect, useState } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import { IoMdCall } from "react-icons/io";
import { IoVideocam } from "react-icons/io5";
import { FaArrowLeftLong, FaArrowRightLong } from "react-icons/fa6";
import { CiClock2 } from "react-icons/ci";
import { IoIosCall } from "react-icons/io";
import { FaHeadphones } from "react-icons/fa6";
import { FaChevronCircleLeft } from "react-icons/fa";
import { FaCircleChevronRight } from "react-icons/fa6";
import CallTransactionDetailsModal from "../components/ConformModel/CallTransactionDetailsModal";

const formatDuration = (mins) => {
  if (!mins && mins !== 0) return "1m";
  const sec = Math.round(mins * 60);
  if (sec < 60) return `${1} min`;
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}m ${s ? `${s}s` : ""}`;
};

const formatDate = (iso) => {
  if (!iso) return "-";
  return new Date(iso).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

export default function UserCallHistory() {
  const { userId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [userInfo, setUserInfo] = useState(null);
  const [history, setHistory] = useState([]);
  const [meta, setMeta] = useState({ currentPage: 1, totalPages: 1 });
  const [page, setPage] = useState(1);
  const [callStats, setCallStats] = useState({
    audioCallsCount: 0,
    audioTotalMinutes: 0,
    videoCallsCount: 0,
    videoTotalMinutes: 0,
  });

  // modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);

  useEffect(() => {
    fetchUserCallHistory();
    fetchAllCallHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, page]);

  const fetchUserCallHistory = async () => {
    try {
      setLoading(true);
      const res = await axios.get(
        `/api/v1/app/call-transactions/getCallHistory/${userId}`,
        { params: { page } }
      );

      if (res.data.status === "OK" && res.data.data) {
        const txs = res.data.data.transactions || [];
        setHistory(txs);
        setMeta({
          currentPage: res.data.data.currentPage || page,
          totalPages: res.data.data.totalPages || 1,
        });

        // find user info inside transactions (callerId or receiverId)
        let foundUser = null;
        for (const t of txs) {
          if (t.callerId && String(t.callerId._id) === String(userId)) {
            foundUser = t.callerId;
            break;
          }
          if (t.receiverId && String(t.receiverId._id) === String(userId)) {
            foundUser = t.receiverId;
            break;
          }
        }

        if (foundUser) {
          setUserInfo(foundUser);
        } else {
          // fallback: fetch user directly from users endpoint
          try {
            const userRes = await axios.get(`/api/v1/admin/users/${userId}`);
            if (userRes?.data) {
              setUserInfo(userRes.data.data || userRes.data);
            }
          } catch (err) {
            console.warn("Failed to fetch single user fallback:", err);
            setUserInfo(null);
          }
        }
      } else {
        setHistory([]);
        setMeta({ currentPage: 1, totalPages: 1 });
        setUserInfo(null);
      }
    } catch (err) {
      console.error("Call history error:", err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch all call history (across all pages) to calculate statistics
  const fetchAllCallHistory = async () => {
    try {
      // Fetch first page to get total pages
      const firstRes = await axios.get(
        `/api/v1/app/call-transactions/getCallHistory/${userId}`,
        { params: { page: 1, limit: 100 } }
      );

      if (firstRes.data.status === "OK" && firstRes.data.data) {
        let allTransactions = [...(firstRes.data.data.transactions || [])];
        const totalPages = firstRes.data.data.totalPages || 1;

        // Fetch remaining pages if any
        if (totalPages > 1) {
          const promises = [];
          for (let p = 2; p <= totalPages; p++) {
            promises.push(
              axios.get(
                `/api/v1/app/call-transactions/getCallHistory/${userId}`,
                { params: { page: p, limit: 100 } }
              )
            );
          }

          const responses = await Promise.all(promises);
          responses.forEach((res) => {
            if (res.data.status === "OK" && res.data.data) {
              allTransactions = allTransactions.concat(res.data.data.transactions || []);
            }
          });
        }

        // Calculate statistics
        const audioCalls = allTransactions.filter(tx => tx.callType === "audio" || tx.callType === "AUDIO");
        const videoCalls = allTransactions.filter(tx => tx.callType === "video" || tx.callType === "VIDEO");

        const audioCallsCount = audioCalls.length;
        const audioTotalMinutes = audioCalls.reduce((sum, call) => sum + (call.durationMinutes || 0), 0);

        const videoCallsCount = videoCalls.length;
        const videoTotalMinutes = videoCalls.reduce((sum, call) => sum + (call.durationMinutes || 0), 0);

        setCallStats({
          audioCallsCount,
          audioTotalMinutes: Math.round(audioTotalMinutes * 100) / 100, // Round to 2 decimal places
          videoCallsCount,
          videoTotalMinutes: Math.round(videoTotalMinutes * 100) / 100,
        });
      }
    } catch (err) {
      console.error("Error fetching all call history for stats:", err);
    }
  };

  const openTransactionModal = (tx) => {
    setSelectedTransaction(tx);
    setIsModalOpen(true);
  };

  const closeTransactionModal = () => {
    setIsModalOpen(false);
    setSelectedTransaction(null);
  };

  if (loading)
    return (
      <div className="text-center p-10 text-[var(--yellow)]">
        Loading call history...
      </div>
    );

  return (
    <div className="p-6 min-h-screen bg-[var(--black)] text-[var(--yellow)]">
      {/* Back Button */}
      <button
        onClick={() => navigate(-1)}
        className="mb-4 flex items-center gap-2 text-[var(--yellow)] hover:opacity-80"
      >
        <FaArrowLeftLong />
        Back
      </button>

      {/* USER PROFILE HEADER */}
      <div className="bg-[var(--black)] border border-[var(--yellow)] p-6 rounded-xl mb-6 shadow">
        <div className="flex gap-6 items-center mb-4">
          <img
            src={userInfo?.profileImage || "https://via.placeholder.com/100"}
            alt="profile"
            className="w-20 h-20 rounded-full border border-[var(--yellow)] object-cover"
          />
          <div>
            <h1 className="text-2xl font-bold">{userInfo?.fullName || "User"}</h1>
            <p className="opacity-80">Gender: {userInfo?.gender || "-"}</p>
            <p className="opacity-80">User ID: {userInfo?._id || userId}</p>
          </div>
        </div>

        {/* Call Statistics */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4 pt-4 border-t border-[var(--yellow)]">
          <div className="flex items-center gap-3">
            <IoMdCall className="text-2xl text-[var(--yellow)]" />
            <div>
              <p className="text-sm opacity-80">Audio Calls Count</p>
              <p className="text-xl font-bold">{callStats.audioCallsCount}</p>
            </div>
            <div className="ml-auto">
              <p className="text-sm opacity-80">Total Minutes</p>
              <p className="text-xl font-bold">{Math.round(callStats.audioTotalMinutes)} min</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <IoVideocam className="text-2xl text-[var(--yellow)]" />
            <div>
              <p className="text-sm opacity-80">Video Calls Count</p>
              <p className="text-xl font-bold">{callStats.videoCallsCount}</p>
            </div>
            <div className="ml-auto">
              <p className="text-sm opacity-80">Total Minutes</p>
              <p className="text-xl font-bold">{Math.round(callStats.videoTotalMinutes)} min</p>
            </div>
          </div>
        </div>
      </div>

      {/* CALL HISTORY LIST */}
      <div className="space-y-4">
        {history.map((call) => (
          <div
            key={call._id}
            onClick={() => openTransactionModal(call)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") openTransactionModal(call);
            }}
            className="cursor-pointer border border-[var(--yellow)] p-4 rounded-xl bg-[var(--black)] shadow hover:shadow-md transition focus:outline-none focus:ring-2 focus:ring-[var(--yellow)]"
          >
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 flex items-center justify-center rounded-full bg-[var(--yellow)] text-[var(--black)]">
                {call.callType === "video" ? (
                  <IoVideocam className="text-3xl" />
                ) : (
                  <IoMdCall className="text-3xl" />
                )}
              </div>

              <div className="flex-1">
                <p className="text-lg font-bold capitalize">{call.callType} Call</p>

                <div className="text-sm opacity-80 mt-1 flex items-center gap-1">
                  <CiClock2 className="text-lg" /> Duration: {formatDuration(call.durationMinutes)}
                </div>

                <p className="text-sm opacity-80">Coins Spent: {call.totalCoins}</p>
                <p className="text-sm opacity-80">Admin Commission: {call.adminCommissionAmount}</p>

                <div className="flex items-center gap-3 mt-2">
                  {/* Caller */}
                  <div className="flex items-center gap-1 text-sm opacity-80">
                    <IoIosCall className="text-lg" />
                    <span className="font-medium">{call.callerId?.fullName || "-"}</span>
                  </div>

                  {/* Arrow */}
                  <FaArrowRightLong className="text-lg gap-3 opacity-70" />

                  {/* Receiver */}
                  <div className="flex items-center gap-3 text-sm opacity-80">
                    <FaHeadphones className=" text-lg" />
                    <span className="font-medium">{call.receiverId?.fullName || "-"}</span>
                  </div>
                </div>
              </div>

              {/* DATE */}
              <div className="text-right text-sm opacity-70 min-w-[120px]">
                {formatDate(call.callStartTime)}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* PAGINATION */}
      {meta.totalPages > 1 && (
        <div className="flex items-center justify-center mt-8">
          <div className="flex items-center gap-2 bg-[var(--black)] border border-[var(--yellow)] px-4 py-3 rounded-xl shadow-lg">
            {/* PREVIOUS */}
            <button
              disabled={page === 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="px-3 py-2 rounded-lg text-sm font-semibold transition bg-[var(--yellow)] text-[var(--black)] disabled:opacity-40 disabled:cursor-not-allowed hover:opacity-80"
            >
              <FaChevronCircleLeft />
            </button>

            {/* PAGE NUMBERS WITH DOTS */}
            <div className="flex items-center gap-2">
              {(() => {
                const totalPages = meta.totalPages;
                let pages = [];

                const createPageBtn = (p) => (
                  <button
                    key={p}
                    onClick={() => setPage(p)}
                    className={`w-8 h-8 flex items-center justify-center rounded-lg text-sm font-semibold border transition 
                      ${
                        p === page
                          ? "bg-[var(--yellow)] text-[var(--black)] border-[var(--yellow)]"
                          : "text-[var(--yellow)] border-[var(--yellow)] hover:bg-[var(--yellow)] hover:text-[var(--black)]"
                      }`}
                  >
                    {p}
                  </button>
                );

                const DOT = (key) => (
                  <span key={key} className="text-[var(--yellow)] font-bold px-2">
                    …
                  </span>
                );

                if (totalPages <= 7) {
                  for (let i = 1; i <= totalPages; i++) pages.push(createPageBtn(i));
                } else {
                  pages.push(createPageBtn(1));
                  pages.push(createPageBtn(2));

                  if (page > 4) pages.push(DOT("start"));

                  let start = Math.max(3, page - 1);
                  let end = Math.min(totalPages - 2, page + 1);

                  for (let i = start; i <= end; i++) pages.push(createPageBtn(i));

                  if (page < totalPages - 3) pages.push(DOT("end"));

                  pages.push(createPageBtn(totalPages - 1));
                  pages.push(createPageBtn(totalPages));
                }

                return pages;
              })()}
            </div>

            {/* NEXT */}
            <button
              disabled={page === meta.totalPages}
              onClick={() => setPage((p) => Math.min(meta.totalPages, p + 1))}
              className="px-3 py-2 rounded-lg text-sm font-semibold transition bg-[var(--yellow)] text-[var(--black)] disabled:opacity-40 disabled:cursor-not-allowed hover:opacity-80"
            >
              <FaCircleChevronRight />
            </button>
          </div>
        </div>
      )}

     
      <CallTransactionDetailsModal
        isOpen={isModalOpen}
        transaction={selectedTransaction}
        onClose={closeTransactionModal}
      />
    </div>
  );
}

